// ═══ WRITE ONLY WHAT CHANGED — make a set of rows match, without deleting and re-inserting them ═══
// (Philip 2026-10-23, activity log stage (b)). The parcel card's deductions save and its Taxes-tab save used to delete
// every row of their scope and insert them all again, so every Save looked like "deleted 3 / added 3" in the activity
// log even when nothing changed. This reads the scope's rows and then:
//   • updates a row only when one of the given columns differs (the log's trigger also skips an update that changes
//     nothing, so a spurious update is harmless — a delete and re-insert is not);
//   • inserts the rows whose key isn't there yet;
//   • deletes the rows whose key is no longer wanted — and any duplicate of a key (the old save could leave none, but
//     a duplicate would otherwise survive forever).
// ⛔ EVERY WRITE CHECKS ITS ROW COUNT: an RLS-refused write returns 0 rows and no error. Updates run first, then deletes,
//    then inserts, so a unique key on (match + key) is never hit by a row that is about to move.
// → { error: null | message, inserted, updated, deleted }
//   db     the supabase client        table  the table      match  { column: value } — the scope (eq filters)
//   key    columns that identify a row within the scope      rows   the wanted rows (each carries match + key + values)
export async function syncRows(db, { table, match, key, rows }) {
  let q = db.from(table).select('*');
  Object.entries(match).forEach(([c, v]) => { q = q.eq(c, v); });
  const { data: existing, error: readErr } = await q;
  if (readErr) return { error: `read: ${readErr.message}`, inserted: 0, updated: 0, deleted: 0 };

  const keyOf = (r) => key.map((c) => String(r[c] ?? '')).join('|');
  const have = new Map();
  const duplicates = [];
  (existing || []).forEach((r) => { const k = keyOf(r); if (have.has(k)) duplicates.push(r.id); else have.set(k, r); });

  const wanted = new Set();
  const inserts = [];
  const updates = [];
  (rows || []).forEach((r) => {
    const k = keyOf(r);
    wanted.add(k);
    const e = have.get(k);
    if (!e) inserts.push(r);
    else if (Object.keys(r).some((c) => !sameValue(e[c], r[c]))) updates.push({ id: e.id, row: r });
  });
  const deletes = [...duplicates, ...[...have.entries()].filter(([k]) => !wanted.has(k)).map(([, r]) => r.id)];

  for (const u of updates) {
    const { data, error } = await db.from(table).update(u.row).eq('id', u.id).select('id');
    if (error || !data?.length) return { error: `update: ${error ? error.message : '0 rows saved'}`, inserted: 0, updated: 0, deleted: 0 };
  }
  if (deletes.length) {
    const { data, error } = await db.from(table).delete().in('id', deletes).select('id');
    if (error || (data?.length ?? 0) !== deletes.length) {
      return { error: `delete: ${error ? error.message : `${data?.length ?? 0} of ${deletes.length} rows removed`}`, inserted: 0, updated: updates.length, deleted: 0 };
    }
  }
  if (inserts.length) {
    const { data, error } = await db.from(table).insert(inserts).select('id');
    if (error || (data?.length ?? 0) !== inserts.length) {
      return { error: `insert: ${error ? error.message : `${data?.length ?? 0} of ${inserts.length} rows saved`}`, inserted: 0, updated: updates.length, deleted: deletes.length };
    }
  }
  return { error: null, inserted: inserts.length, updated: updates.length, deleted: deletes.length };
}

// Equal as the database would store them: null / undefined / '' are all empty; numbers compare as numbers ('100' = 100);
// objects and arrays compare by content whatever their key order (jsonb comes back with its keys re-ordered).
export function sameValue(a, b) {
  const empty = (v) => v === null || v === undefined || v === '';
  if (empty(a) || empty(b)) return empty(a) && empty(b);
  if (typeof a === 'object' || typeof b === 'object') return stable(a) === stable(b);
  const na = Number(a); const nb = Number(b);
  if (Number.isFinite(na) && Number.isFinite(nb) && String(a).trim() !== '' && String(b).trim() !== '') return na === nb;
  return String(a) === String(b);
}
const stable = (v) => (v === null || typeof v !== 'object' ? JSON.stringify(v ?? null)
  : Array.isArray(v) ? `[${v.map(stable).join(',')}]`
  : `{${Object.keys(v).filter((k) => v[k] !== undefined).sort().map((k) => `${JSON.stringify(k)}:${stable(v[k])}`).join(',')}}`);
